/**
 * S10 — Éditeur de paliers de prix dégressifs
 * Composant pour configurer les tarifs dégressifs d'un produit
 * Design Dashboard : bg-white, text-gray-900, teal-600
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, TrendingDown, Info, AlertCircle } from 'lucide-react';
import { productsAPI } from '@/utils/productsAPI';
import { resolveTierPrice } from '@/utils/priceTierUtils';
import type { PricingMode } from '@/types/models';

interface TierRow {
  minQuantity: number;
  unitPrice: number;
}

interface PriceTierEditorProps {
  productId: string;
  baseUnitPrice: number;
  initialPricingMode?: PricingMode;
  initialTiers?: TierRow[];
  onChange?: (pricingMode: PricingMode, tiers: TierRow[]) => void;
}

// Query key centralisée pour les paliers de prix
const tierKeys = {
  all: ['priceTiers'] as const,
  detail: (productId: string) => [...tierKeys.all, productId] as const,
};

export const PriceTierEditor: React.FC<PriceTierEditorProps> = ({
  productId,
  baseUnitPrice,
  initialPricingMode,
  initialTiers,
  onChange,
}) => {
  const { t } = useTranslation('products');
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(false);
  const [pricingMode, setPricingMode] = useState<PricingMode>(initialPricingMode || 'VOLUME');
  const [tiers, setTiers] = useState<TierRow[]>(initialTiers || []);
  const [previewQty, setPreviewQty] = useState<number>(100);

  // Charger les paliers existants via useQuery
  const { data: tierData, isLoading: loadingTiers, error: fetchError } = useQuery({
    queryKey: tierKeys.detail(productId),
    queryFn: () => productsAPI.getTiers(productId),
    enabled: !!productId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Synchroniser l'état local quand les données arrivent
  useEffect(() => {
    if (tierData && tierData.tiers.length > 0) {
      setEnabled(true);
      setPricingMode(tierData.pricingMode);
      setTiers(tierData.tiers.map(t => ({
        minQuantity: Number(t.minQuantity),
        unitPrice: Number(t.unitPrice),
      })));
    }
  }, [tierData]);

  // Mutation pour sauvegarder les paliers
  const saveMutation = useMutation({
    mutationFn: (data: { pricingMode: PricingMode; tiers: TierRow[] }) =>
      productsAPI.setTiers(productId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tierKeys.detail(productId) });
    },
  });

  const handleToggle = (checked: boolean) => {
    setEnabled(checked);
    if (!checked) {
      setTiers([]);
      onChange?.(pricingMode, []);
    }
  };

  const handleModeChange = (mode: PricingMode) => {
    setPricingMode(mode);
    onChange?.(mode, tiers);
  };

  const handleAddTier = () => {
    const lastQty = tiers.length > 0 ? tiers[tiers.length - 1].minQuantity : 0;
    const newTier: TierRow = {
      minQuantity: lastQty + 10,
      unitPrice: baseUnitPrice * 0.9,
    };
    const newTiers = [...tiers, newTier];
    setTiers(newTiers);
    onChange?.(pricingMode, newTiers);
  };

  const handleRemoveTier = (index: number) => {
    const newTiers = tiers.filter((_, i) => i !== index);
    setTiers(newTiers);
    onChange?.(pricingMode, newTiers);
  };

  const handleTierChange = (index: number, field: keyof TierRow, value: number) => {
    const newTiers = tiers.map((t, i) => i === index ? { ...t, [field]: value } : t);
    setTiers(newTiers);
    onChange?.(pricingMode, newTiers);
  };

  const handleSave = () => {
    if (!enabled || tiers.length === 0) {
      saveMutation.mutate({ pricingMode, tiers: [] });
    } else {
      saveMutation.mutate({ pricingMode, tiers });
    }
  };

  // Aperçu en temps réel
  const preview = enabled && tiers.length > 0
    ? resolveTierPrice(previewQty, baseUnitPrice, pricingMode, tiers)
    : null;

  if (loadingTiers) {
    return (
      <div className="flex items-center gap-2 py-2">
        <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-gray-500">{t('products.tiers.loading', 'Chargement des paliers...')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingDown className="w-4 h-4 text-teal-600" />
          <span className="text-sm font-semibold text-gray-900">
            {t('products.tiers.title')}
          </span>
        </div>
        <label className="flex items-center cursor-pointer gap-2">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => handleToggle(e.target.checked)}
            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
          />
          <span className="text-sm text-gray-700">{t('products.tiers.enable')}</span>
        </label>
      </div>

      {/* Erreur de chargement */}
      {fetchError && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {t('products.tiers.loadError', 'Erreur lors du chargement des paliers')}
        </div>
      )}

      {enabled && (
        <>
          {/* Mode selection */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => handleModeChange('VOLUME')}
              className={`flex-1 px-3 py-2 text-sm rounded-xl border transition-all ${
                pricingMode === 'VOLUME'
                  ? 'border-teal-500 bg-teal-50 text-teal-700 font-medium'
                  : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
              }`}
            >
              {t('products.tiers.modeVolume')}
            </button>
            <button
              type="button"
              onClick={() => handleModeChange('GRADUATED')}
              className={`flex-1 px-3 py-2 text-sm rounded-xl border transition-all ${
                pricingMode === 'GRADUATED'
                  ? 'border-teal-500 bg-teal-50 text-teal-700 font-medium'
                  : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
              }`}
            >
              {t('products.tiers.modeGraduated')}
            </button>
          </div>

          {/* Mode description */}
          <div className="flex items-start gap-2 bg-gray-50 rounded-xl p-3">
            <Info className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-gray-500">
              {pricingMode === 'VOLUME'
                ? t('products.tiers.volumeDesc')
                : t('products.tiers.graduatedDesc')}
            </p>
          </div>

          {/* Tiers table */}
          <div className="space-y-2">
            {tiers.map((tier, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="flex-1">
                  <label className="text-xs text-gray-500 mb-1 block">
                    {t('products.tiers.fromQuantity')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={tier.minQuantity}
                    onChange={(e) => handleTierChange(index, 'minQuantity', parseFloat(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-gray-500 mb-1 block">
                    {t('products.tiers.unitPrice')}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={tier.unitPrice}
                      onChange={(e) => handleTierChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">€</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveTier(index)}
                  className="mt-5 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Add tier button */}
          {tiers.length < 20 && (
            <button
              type="button"
              onClick={handleAddTier}
              className="flex items-center gap-1 px-3 py-2 text-sm text-teal-600 hover:bg-teal-50 rounded-xl transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t('products.tiers.addTier')}
            </button>
          )}

          {/* Preview */}
          {tiers.length > 0 && (
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-teal-700">{t('products.tiers.preview')}</span>
                <input
                  type="number"
                  min="1"
                  value={previewQty}
                  onChange={(e) => setPreviewQty(parseFloat(e.target.value) || 1)}
                  className="w-20 px-2 py-1 border border-teal-300 rounded-lg text-xs text-center focus:ring-1 focus:ring-teal-500"
                />
                <span className="text-xs text-teal-600">{t('products.tiers.units')}</span>
              </div>
              {preview && (
                <div className="text-sm text-teal-800">
                  {preview.tierApplied ? (
                    <span>
                      <span className="line-through text-teal-500 mr-1">{baseUnitPrice.toFixed(2)}€</span>
                      → <span className="font-bold">{preview.effectiveUnitPrice.toFixed(2)}€</span>/u
                      <span className="text-xs text-teal-600 ml-1">
                        ({t('products.tiers.saving')} {((1 - preview.effectiveUnitPrice / baseUnitPrice) * 100).toFixed(1)}%)
                      </span>
                    </span>
                  ) : (
                    <span>{baseUnitPrice.toFixed(2)}€/u ({t('products.tiers.noTierApplied')})</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Error */}
          {saveMutation.error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {(saveMutation.error as Error).message || t('products.tiers.saveError')}
            </div>
          )}

          {/* Save button */}
          {productId && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saveMutation.isPending}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-xl transition-colors disabled:opacity-50"
            >
              {saveMutation.isPending ? t('products.tiers.saving') + '...' : t('products.tiers.save')}
            </button>
          )}
        </>
      )}
    </div>
  );
};
